import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';
import { getFileParts } from '@/stack-shared/utils/gen-utils';
import { getFileExtension } from '@/utils/general';
import { useInitPresignedUrlMultipartUpload, useInitPresignedUrlSimpleUpload, useCompleteMultipartUpload } from './useAws';
import { ICreateUploadResourcePayload } from '@/stack-shared/interfaces/model/resource.interfaces';

const httpClient = new HttpClient();

interface GetResourcesByModelParams {
  model: string;
  modelId: number;
}

const getResourcesByModelApi = async (params: GetResourcesByModelParams): Promise<any[]> => {
  const response = await httpClient.get('api/resource/get-by-model', params);
  return response.data.data || response.data || [];
};

const createUploadResourceApi = async (payload: ICreateUploadResourcePayload): Promise<any> => {
  const response = await httpClient.post('api/resource/create-upload-resource', payload);
  return response.data;
};

const deleteResourceApi = async (payload: { id: number }): Promise<any> => {
  const response = await httpClient.post('api/resource/delete', payload);
  return response.data;
};

const togglePrimaryResourceApi = async (params: { id: number }): Promise<any> => {
  const response = await httpClient.get('api/resource/toggle-primary', params);
  return response.data;
};

const convertResourcesApi = async (payload: { id: number; conversions: any[]; queue?: boolean }): Promise<any> => {
  const response = await httpClient.post('api/resource/convert', payload);
  return response.data;
};

export const useResourcesByModel = (model: string, modelId: number) => {
  return useQuery({
    queryKey: ['resources', model, modelId],
    queryFn: () => getResourcesByModelApi({ model, modelId }),
    enabled: !!model && !!modelId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useCreateUploadResource = () => {
  return useMutation({
    mutationFn: createUploadResourceApi,
  });
};

export const useDeleteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteResourceApi,
    onSuccess: (data, variables) => {
      // Invalidate resources queries
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
};

export const useTogglePrimaryResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: togglePrimaryResourceApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
};

export const useConvertResources = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: convertResourcesApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
};

interface UploadResourceFilePayload {
  file: File;
  model: string;
  modelId: number;
  conversions?: any[];
  queue?: boolean;
  onProgress?: (percent: number) => void;
}

export const useUploadResourceFile = () => {
  const queryClient = useQueryClient();
  const createUploadResource = useCreateUploadResource();
  const initMultipartUpload = useInitPresignedUrlMultipartUpload();
  const initSimpleUpload = useInitPresignedUrlSimpleUpload();
  const completeMultipartUpload = useCompleteMultipartUpload();
  const convertResources = useConvertResources();

  return useMutation({
    mutationFn: async (payload: UploadResourceFilePayload) => {
      const { file, model, modelId, conversions, queue, onProgress } = payload;

      // Step 1: Create upload resource
      const uploadResourceData: ICreateUploadResourcePayload = {
        model,
        modelId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      };

      const createResult = await createUploadResource.mutateAsync(uploadResourceData);
      const urls = createResult.urls;
      const uploadId = createResult.uploadId;
      const resource = createResult.resource;

      const parts = getFileParts(file);
      const extension = getFileExtension(file.name);

      if (parts > 1) {
        // Multipart upload
        const completedParts = await initMultipartUpload.mutateAsync({
          file,
          urls: urls as string[],
          onProgress,
        });

        const key = `test/${file.name}.${extension}`;
        await completeMultipartUpload.mutateAsync({
          key,
          uploadId: uploadId ?? '',
          parts: completedParts,
        });
      } else {
        // Simple upload
        await initSimpleUpload.mutateAsync({
          file,
          url: urls![0] as string,
          onProgress,
        });

        // Convert resources if needed
        if (conversions) {
          await convertResources.mutateAsync({
            id: resource.id,
            conversions,
            queue: queue || false,
          });
        }
      }

      // Invalidate resources queries
      queryClient.invalidateQueries({ queryKey: ['resources'] });

      return { resource, ...createResult };
    },
  });
};
